import { useMemo } from "react";
import { useTimeDifference } from "hooks/useTimeDifference";
import { appStore } from "store";
import { computeSaleProgressMetrics } from "utils/saleProgress";
import {
  FALLBACK_LISTING_TOKEN_PRICE_USD,
  FALLBACK_NEXT_TOKEN_PRICE_USD,
  formatPresaleCurrentUsdLabel,
  formatPresaleUsdLabel,
  formatUsdCompact,
  toFiniteNumber,
} from "utils/presaleDisplay";

/** Compute progress relative to the current round only (not the entire presale pool). */
function computeRoundProgress(currentRound: Parameters<typeof computeSaleProgressMetrics>[0], sold: number, totalPool: number) {
  const tokenPrice = toFiniteNumber(currentRound?.tokenPrice) ?? 0;
  const roundSold = Number(currentRound?.sold ?? 0);
  const roundPool = Number(currentRound?.pool ?? 0);

  if (roundPool > 0 && tokenPrice > 0) {
    const raisedUsd = roundSold * tokenPrice;
    const targetUsd = roundPool * tokenPrice;
    return { raisedUsd, targetUsd, percent: Math.min(100, (raisedUsd / targetUsd) * 100) };
  }

  // Fallback to global API values when round data is unavailable
  const m = computeSaleProgressMetrics(currentRound, sold, totalPool);
  return { raisedUsd: m.raisedUsd, targetUsd: m.targetUsd, percent: m.percent };
}

export type PresaleHeroTimer = {
  d: number;
  h: number;
  m: number;
  s: number;
};

/**
 * Live presale figures from `appStore` (same source as dashboard / payment flow).
 */
export function usePresaleHeroData() {
  const { currentRound, isLoading, totalPool, sold } = appStore();

  const hasValidEnd = Boolean(
    currentRound?.finishAt &&
      !Number.isNaN(new Date(currentRound.finishAt).getTime()),
  );

  const endTs = hasValidEnd
    ? new Date(currentRound!.finishAt).getTime()
    : Date.now();

  const { days, hours, minutes, seconds } = useTimeDifference(
    endTs,
    !isLoading && hasValidEnd,
  );

  const timer: PresaleHeroTimer = useMemo(
    () => ({
      d: days,
      h: hours,
      m: typeof minutes === "string" ? parseInt(minutes, 10) : Number(minutes),
      s: typeof seconds === "string" ? parseInt(seconds, 10) : Number(seconds),
    }),
    [days, hours, minutes, seconds],
  );

  const prices = useMemo(
    () => ({
      current: formatPresaleCurrentUsdLabel(currentRound?.tokenPrice),
      next: formatPresaleUsdLabel(
        toFiniteNumber(currentRound?.nextTokenPrice) ??
          FALLBACK_NEXT_TOKEN_PRICE_USD,
      ),
      listing: formatPresaleUsdLabel(
        toFiniteNumber(currentRound?.listingTokenPrice) ??
          FALLBACK_LISTING_TOKEN_PRICE_USD,
      ),
    }),
    [currentRound],
  );

  const progress = useMemo(() => {
    const m = computeRoundProgress(currentRound, sold, totalPool);
    return {
      raisedUsd: m.raisedUsd,
      poolUsd: m.targetUsd,
      percent: m.percent,
    };
  }, [currentRound, sold, totalPool]);

  const raisedLabel = formatUsdCompact(progress.raisedUsd);
  const poolLabel = formatUsdCompact(progress.poolUsd);

  return {
    currentRound,
    isLoading,
    timer,
    prices,
    progress,
    raisedLabel,
    poolLabel,
    pad: (n: number) => n.toString().padStart(2, "0"),
  };
}
